import * as React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { login as authSliceLogin } from '../../../redux/features/authSlice';
import { useGetAuthModeQuery, useLoginMutation } from '../../../redux/services/auth';
import { RootAuthState } from '../../authProvider';
import AuthActionButton from '../components/auth-action-button';
import AuthErrorMessage from '../components/auth-error-message';
import AuthFormInput from '../components/auth-form-input';
import AuthLink from '../components/auth-link';
import AuthPageWrapper from '../components/auth-page-wrapper';
import GoogleAuthButton from '../components/google-auth-button';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [login] = useLoginMutation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: authMode } = useGetAuthModeQuery();
  const { authenticated } = useSelector((state: RootAuthState) => state.auth);
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');
  const googleNextPath = returnTo ? decodeURIComponent(returnTo) : '/';
  const isCognitoEnabled = authMode?.data?.isCognitoEnabled === true;

  React.useEffect(() => {
    if (authenticated) {
      navigate('/');
    }
  }, [authenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    const formData = new FormData(e.currentTarget);

    try {
      await login({
        email: formData.get('email') as string,
        password: formData.get('password') as string,
        rememberMe: formData.get('rememberMe') === 'on',
      }).unwrap();
      dispatch(authSliceLogin());

      if (returnTo) {
        navigate(decodeURIComponent(returnTo));
      } else {
        navigate('/');
      }
    } catch (error: any) {
      setLoading(false);
      if (error?.data?.code === 401) {
        setError('Invalid email or password. Please try again.');
        return;
      }

      if (error?.data?.code === 404) {
        setError('No account found with this email address.');
        return;
      }
      setError(
        error?.data?.message ||
          'An unexpected error occurred. Please try again.'
      );
    }
  };

  return (
    <AuthPageWrapper
      title='Welcome back to Ghost News'
      subtitle='Ready to dive back into the supernatural?'
    >
      {/* Right: Login Form */}

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className='flex flex-column gap-3 w-full'
      >
        <h1 className='text-2xl font-medium mb-1'>Log in to your account</h1>

        <AuthFormInput
          id='email'
          name='email'
          type='email'
          label='Email'
          placeholder='Enter your email'
          required
          icon='pi pi-envelope'
        />

        <AuthFormInput
          id='password'
          name='password'
          type='password'
          label='Password'
          placeholder='Enter your password'
          required
          icon='pi pi-lock'
        />

        <div className='flex align-items-center justify-content-between'>
          <div className='flex align-items-center'>
            <input
              type='checkbox'
              id='rememberMe'
              name='rememberMe'
              className='mr-2'
            />
            <label htmlFor='rememberMe' className='text-700 text-sm'>
              Remember me
            </label>
          </div>
          <AuthLink linkText='Forgot password?' to='/forgot-password' />
        </div>

        <AuthErrorMessage error={error} />

        <AuthActionButton label='Log In' loading={loading} disabled={loading} />

        {isCognitoEnabled && (
          <>
            <div className='flex align-items-center my-2'>
              <div className='flex-1 border-top-1 border-300' />
              <span className='px-3 text-500 text-sm'>or</span>
              <div className='flex-1 border-top-1 border-300' />
            </div>

            <GoogleAuthButton
              mode='login'
              nextPath={googleNextPath}
              disabled={loading}
              className='w-full'
            />
          </>
        )}

        <AuthLink
          text="Don't have an account? "
          linkText='Sign up'
          to='/sign-up'
          className='text-left mt-2'
        />
      </form>
    </AuthPageWrapper>
  );
};
